import ignore from 'ignore';
import { selectionStore } from '~/lib/persistence/selectionStore';
import type { Framework, Language } from '~/lib/stores/techStackSelection';
import type { ActionCallbackData } from '~/lib/runtime/message-parser';
import { CHECK_IGNORE_FILE, IGNORE_FILE_CONTENT_FOR_WORKBENCH, isAssetFile } from './fileIgnore';
import * as Sentry from '@sentry/remix';
import { Octokit } from '@octokit/rest';
import axios from 'axios';
import { isDev } from '~/utils/constants';

// Define GitHubFile interface locally or import if defined elsewhere and accessible
interface GitHubFile {
  name: string;
  path: string;
  content: string;
}

interface ApiErrorResponse {
  error: string;
  details?: string;
  retryAfter?: number; // Added for rate limit information
}

interface ApiFilesResponse {
  files: GitHubFile[];
  error?: undefined;
}

interface RateLimitError extends Error {
  retryAfter?: number;
  isRateLimit: boolean;
}

export interface AssistantMessageBuilderParams {
  repoInfo: {
    owner: string;
    repo: string;
    branch: string;
  };
  filteredFiles: Array<{
    path: string;
    content: string;
    name: string;
    dualiteIgnore: boolean;
  }>;
  isAssetFile: (path: string) => boolean;
  isExamplePrompt?: boolean;
}

export type AssistantMessageBuilder = (params: AssistantMessageBuilderParams) => string;

// Helper to create a rate limit error
function createRateLimitError(message: string, retryAfter?: number): RateLimitError {
  const error = new Error(message) as RateLimitError;
  error.isRateLimit = true;
  error.retryAfter = retryAfter;
  return error;
}

async function fetchFromGitHubAPI(
  framework: Framework,
  language: Language,
  isDevContext: boolean,
): Promise<GitHubFile[]> {
  console.log(
    `[getTemplates] Calling API to fetch templates for framework=${framework}, language=${language}, isDevContext=${isDevContext}`,
  );

  try {
    const { data, status, statusText, headers } = await axios.get<ApiFilesResponse | ApiErrorResponse>(
      `/api/get-templates?framework=${framework}&language=${language}&isDevContext=${isDevContext}`,
    );

    // Check for rate limit headers
    const rateLimitRemaining = headers['x-ratelimit-remaining'];
    const rateLimitReset = headers['x-ratelimit-reset'];

    if (rateLimitRemaining && parseInt(rateLimitRemaining) < 3) {
      const resetTime = rateLimitReset ? new Date(parseInt(rateLimitReset) * 1000) : undefined;
      console.warn(
        `[getTemplates] Rate limit warning: Only ${rateLimitRemaining} requests remaining. Reset at ${resetTime?.toLocaleString()}`,
      );
    }

    if (status === 429) {
      const retryAfter = headers['retry-after'] ? parseInt(headers['retry-after']) : undefined;
      const resetTime = retryAfter ? new Date(Date.now() + retryAfter * 1000).toLocaleString() : 'unknown time';

      throw createRateLimitError(`GitHub API rate limit exceeded. Please try again after ${resetTime}`, retryAfter);
    }

    if (status !== 200) {
      const errorMsg = `API Error: ${status} ${statusText} - ${(data as ApiErrorResponse).error || 'Unknown error'}`;
      console.error(`[getTemplates] ${errorMsg}`, data);
      Sentry.captureMessage(errorMsg, {
        level: 'error',
        extra: { framework, language, status, responseBody: data },
      });
      throw new Error(errorMsg);
    }

    if ('error' in data) {
      const errorData = data as ApiErrorResponse;

      // Check if it's a rate limit error from the API
      if (errorData.error?.toLowerCase().includes('rate limit') && errorData.retryAfter) {
        throw createRateLimitError(errorData.error, errorData.retryAfter);
      }

      const errorMsg = `API returned error: ${errorData.error}`;
      console.error(`[getTemplates] ${errorMsg}`, errorData.details);
      Sentry.captureMessage(errorMsg, {
        level: 'error',
        extra: { framework, language, details: errorData.details },
      });
      throw new Error(errorMsg);
    }

    if (!data.files?.length) {
      const msg = 'API response did not contain files';
      console.error(`[getTemplates] ${msg}`, data);
      Sentry.captureMessage(msg, {
        level: 'error',
        extra: { framework, language, responseData: data },
      });
      throw new Error(msg);
    }

    console.log(`[getTemplates] Successfully fetched ${data.files.length} files via API`);
    return data.files;
  } catch (error) {
    console.error(`[getTemplates] Error calling fetchFromGitHubAPI:`, error);

    // Check if it's already a rate limit error
    if ((error as RateLimitError).isRateLimit) {
      throw error;
    }

    // Check if it's an Axios error with rate limit status
    if (axios.isAxiosError(error) && error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const resetTime = retryAfter
        ? new Date(Date.now() + parseInt(retryAfter) * 1000).toLocaleString()
        : 'unknown time';

      throw createRateLimitError(
        `GitHub API rate limit exceeded. Please try again after ${resetTime}`,
        retryAfter ? parseInt(retryAfter) : undefined,
      );
    }

    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      extra: {
        framework,
        language,
        function: 'fetchFromGitHubAPI',
        isRateLimit: (error as RateLimitError).isRateLimit || false,
      },
    });

    throw error;
  }
}

export async function getTemplates(framework: Framework, language: Language, title?: string) {
  console.log(`[getTemplates] Starting getTemplates for framework=${framework}, language=${language} title=${title}`);

  let files: GitHubFile[];
  const startTime = Date.now();

  // Determine if we're in development context based on AI model
  const aiModel = selectionStore.get().aiModel;

  let isDevContext = true;

  if (isDev && (aiModel === 'dev-ai-model' || aiModel === 'dev-mock-ai-model')) {
    isDevContext = false;
  }

  console.log(`[getTemplates] Using AI Model: ${aiModel}, isDevContext: ${isDevContext}`);

  console.log(`[getTemplates] Beginning GitHub fetch via API at ${new Date().toISOString()}`);

  try {
    files = await fetchFromGitHubAPI(framework, language, isDevContext);
    console.log(`[getTemplates] API fetch completed in ${files} : ${files.length} files`);
  } catch (error) {
    console.error(`[getTemplates] Failed to fetch template from API:`, error);

    // Check if it's a rate limit error and enhance the error message
    if ((error as RateLimitError).isRateLimit) {
      const rateLimitError = error as RateLimitError;
      const retryAfter = rateLimitError.retryAfter;
      const resetTime = retryAfter ? new Date(Date.now() + retryAfter * 1000).toLocaleString() : 'unknown time';

      throw new Error(
        `GitHub API rate limit exceeded. Please try again after ${resetTime}. This helps ensure stable service for all users.`,
      );
    }

    throw new Error('Failed to fetch template');
  }

  console.log(`[getTemplates] Starting template processing for ${files.length} files`, files);

  const filesToImport: Array<{ path: string; content: string; name: string; dualiteIgnore: boolean }> = [];

  for (const file of files) {
    if (file.path.includes('node_modules')) {
      console.log(`Skipping hidden or unnecessary file: ${file.path}`);
      continue;
    }

    try {
      console.log('[getTemplates] content', file.content);

      if (CHECK_IGNORE_FILE(file.path)) {
        console.log(`[getTemplates] Ignoring file: ${file.path}`);
        filesToImport.push({
          name: file.path,
          path: file.path,
          content: file.content,
          dualiteIgnore: true,
        });
      } else {
        filesToImport.push({
          name: file.path,
          path: file.path,
          content: file.content,
          dualiteIgnore: false,
        });
      }

      console.log(`[getTemplates] Adding ${filesToImport.length} ignored files to workbench store`, filesToImport);

      /*
       * @sayan why we had written undefined in id and title? Reverting it for now because it doesn't make sense.
       * workbenchStore.addArtifact({
       *   id: 'import-ignored-files',
       *   title: 'Imported Files Not in Prompt',
       *   messageId: 'import-files-not-in-prompt',
       * });
       */

      /*
       * const data: ActionCallbackData = {
       *   artifactId: 'import-ignored-files',
       *   messageId: `import-files-not-in-prompt`,
       *   actionId: `action-${Date.now()}-${file.path}`,
       *   action: {
       *     type: 'file',
       *     filePath: file.path,
       *     content: file.content,
       *   },
       * };
       * workbenchStore.addAction(data);
       * workbenchStore.runAction(data);
       */
    } catch (err) {
      console.error(`Failed to fetch content for ${file.path}:`, err);
    }
  }

  console.log(`[getTemplates] Generating assistant message with ${filesToImport.length} files`);

  const assistantMessage = `
<dualiteArtifact id="untitled-project" title="${title || 'Importing Starter Files'}" type="bundled">
${filesToImport
  .map(
    (file) =>
      `<dualiteAction type="file" filePath="${file.path}" ${file.dualiteIgnore ? '.dualiteIgnore' : ''}>
${file.content}
</dualiteAction>`,
  )
  .join('\n')}
</dualiteArtifact> 
`;

  console.log(`[getTemplates] Assistant message generated (${assistantMessage.length} chars)`);

  let userMessage = '';
  userMessage += `
  ---
  Template files have been imported successfully.
  REPOSITORY STRUCTURE:
  Here's a list of all files in the repository:
  ${filesToImport.map((file) => `- ${file.path}`).join('\n')}
  
  Here are your next steps:
  DEVELOPMENT SETUP (Using given package manager):
  1. Install project dependencies
  2. Start development server

  ${
    framework === 'react_shadcn'
      ? `COMPONENT SETUP:
  To use Shadcn UI components, install them first:
  \`npx --yes shadcn@latest add <component-1-name> <component-2-name>\`
  `
      : ''
  }

  DEVELOPMENT GUIDELINES:
  • You can modify any existing files
  • Create new files as needed
  • Use any imported components and utilities
  • Follow the project's coding standards

  Please proceed with the implementation based on the requirements provided.
  ---
  `;

  if (filesToImport.length > 0) {
    console.log(`[getTemplates] Adding ${filesToImport.length} ignored files information to user message`);
    userMessage =
      userMessage +
      `
  NOTE: FOLLOWING FILES ARE PRESENT IN THE SYSTEM BUT NOT SHOWN TO YOU BUT YOU CAN IMPORT THEM AND USE THEM IN YOUR APP:
${filesToImport
  .reduce((acc, file) => {
    if (file.dualiteIgnore) {
      acc.push(`- ${file.path}`);
    }

    return acc;
  }, [] as string[])
  .join('\n')}

  Permitted actions:
  ✓ Import these files and use them in your app

  If you need to make changes to functionality, create new files instead of modifying the protected ones listed above.
  ---
  `;
  }

  console.log(`[getTemplates] User message generated (${userMessage.length} chars)`);
  console.log(`[getTemplates] Template processing completed in ${Date.now() - startTime}ms`);

  return {
    assistantMessage,
    userMessage,
  };
}

export const extractRepoInfo = (url: string, branch?: string) => {
  try {
    const parsedUrl = new URL(url);
    const parts = parsedUrl.pathname.replace(/^\/|\/$/g, '').split('/');

    if (parts.length < 2) {
      return null;
    }

    const owner = parts[0];
    const repo = parts[1];

    // Extract branch from URL if present
    branch = branch || 'main'; // default branch

    const branchMatches = parsedUrl.pathname.match(/\/(tree|blob|branches)\/([^/]+)/);

    if (branchMatches && branchMatches[2]) {
      branch = decodeURIComponent(branchMatches[2]);
    }

    if (!owner || !repo) {
      return null;
    }

    console.log(`Extracted repo info - owner: ${owner}, repo: ${repo}, branch: ${branch}`);
    return { owner, repo, branch };
  } catch (err) {
    console.error('>>>[extractRepoInfo] error: ', err);
    Sentry.captureException(err, { extra: { url, function: 'extractRepoInfo' } });
    return null;
  }
};

export const sanitizePostgresString = (text: string) => {
  // Replace null bytes with an empty string or a placeholder
  return text.replace(/\u0000/g, '');
};

const fetchRepositoryContent = async (
  repoUrl: string,
  repoInfo: { owner: string; repo: string; branch: string },
): Promise<Array<{ path: string; content: string; name: string; dualiteIgnore: boolean }> | undefined> => {
  const repoFiles: Array<{ path: string; download_url: string }> = [];
  const repoFilesWContent: Array<{ path: string; content: string; name: string; dualiteIgnore: boolean }> = [];
  const accessToken = selectionStore.get().githubAccessToken;

  // Initialize Octokit
  const octokit = new Octokit({
    auth: accessToken,
  });

  if (!repoInfo) {
    console.log('Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository');
    return undefined;
  }

  try {
    // Verify branch exists using Octokit
    try {
      await octokit.repos.getBranch({
        owner: repoInfo.owner,
        repo: repoInfo.repo,
        branch: repoInfo.branch,
      });
    } catch (error) {
      console.log(`Branch "${repoInfo.branch}" not found, falling back to default branch`);

      // Fetch default branch using Octokit
      const { data: repoData } = await octokit.repos.get({
        owner: repoInfo.owner,
        repo: repoInfo.repo,
      });

      repoInfo.branch = repoData.default_branch;
      console.log(`Using default branch: ${repoInfo.branch}`);
    }

    // Recursive fetcher using Octokit
    const fetchDirectory = async (path = '') => {
      try {
        const { data } = await octokit.repos.getContent({
          owner: repoInfo.owner,
          repo: repoInfo.repo,
          path,
          ref: repoInfo.branch,
        });

        const items = Array.isArray(data) ? data : [data];

        for (const item of items) {
          if (item.type === 'file') {
            repoFiles.push({
              path: item.path,
              download_url: item.download_url || '',
            });
          } else if (item.type === 'dir') {
            await fetchDirectory(item.path);
          }
        }
      } catch (err) {
        console.error(`Error fetching path "${path}":`, err);
        throw err;
      }
    };

    await fetchDirectory();

    const artifactId = `${repoInfo.repo}`;

    /*
     * workbenchStore.addArtifact({
     *   id: artifactId,
     *   title: artifactId || 'GitHub Import',
     *   messageId: 'github-files-import',
     * });
     */

    // Process each file
    for (const file of repoFiles) {
      if (file.path.includes('node_modules')) {
        console.log(`Skipping hidden or unnecessary file: ${file.path}`);
        continue;
      }

      try {
        const { data } = await octokit.repos.getContent({
          owner: repoInfo.owner,
          repo: repoInfo.repo,
          path: file.path,
          ref: repoInfo.branch,
        });

        console.log('>>>[fetchRepositoryContent] data: ', data);

        let content: string;

        if (isAssetFile(file.path) || IGNORE_FILE_CONTENT_FOR_WORKBENCH(file.path)) {
          // For binary files, fetch from download URL and convert to base64
          const response = await axios.get(file.download_url, { responseType: 'arraybuffer' });
          const base64Content = Buffer.from(response.data).toString('base64');
          content = `data:application/octet-stream;base64,${base64Content}`;
          console.log(`[getTemplates] Binary file processed: ${file.path} (base64 encoded from download URL)`);
        } else {
          // For text files, decode the base64 content
          content = Buffer.from((data as any).content, 'base64').toString();
          console.log(`[getTemplates] Text file processed: ${file.path}`);
        }

        content = sanitizePostgresString(content);

        console.log('[getTemplates] content', content);

        if (CHECK_IGNORE_FILE(file.path)) {
          console.log(`[getTemplates] Ignoring file: ${file.path}`);
          repoFilesWContent.push({
            name: file.path,
            path: file.path,
            content,
            dualiteIgnore: true,
          });
        } else {
          repoFilesWContent.push({
            name: file.path,
            path: file.path,
            content,
            dualiteIgnore: false,
          });
        }

        const actionData: ActionCallbackData = {
          artifactId,
          messageId: 'github-files-import',
          actionId: `github-import-${Date.now()}-${file.path}`,
          action: {
            type: 'file',
            filePath: file.path,
            content,
          },
        };

        /*
         * workbenchStore.addAction(actionData);
         * workbenchStore.runAction(actionData);
         */
      } catch (err) {
        console.error(`Failed to fetch content for ${file.path}:`, err);
      }
    }

    return repoFilesWContent;
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        function: 'Error in fetchRepositoryContent',
        repoUrl,
        repoInfo,
      },
    });
    console.log(err instanceof Error ? err.message : 'An unexpected error occurred while fetching the repository.');
    console.error(err);
    return undefined;
  }
};

interface TemplateResult {
  assistantMessage: string;
  userMessage: string;
}

export async function getGithubCode(
  framework: Framework,
  language: Language,
  githubRepo: string,
  branch?: string,
  isExamplePrompt?: boolean,
  assistantMessageBuilder?: AssistantMessageBuilder,
): Promise<TemplateResult> {
  console.log('>>>[getGithubCode] githubRepo: ', githubRepo, 'branch: ', branch);

  if (!githubRepo) {
    console.log('Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository');
    return { assistantMessage: '', userMessage: '' };
  }

  const startTime = Date.now();
  const repoInfo = extractRepoInfo(githubRepo, branch);
  console.log('>>>[getGithubCode] repoInfo: ', repoInfo);

  if (!repoInfo) {
    console.log('Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository');
    return { assistantMessage: '', userMessage: '' };
  }

  const files = isExamplePrompt
    ? await fetchRepositoryContentViaAPI(githubRepo, repoInfo)
    : await fetchRepositoryContent(githubRepo, repoInfo);

  console.log('>>>[getTemplates] files: ', files);

  if (!files) {
    return { assistantMessage: '', userMessage: '' };
  }

  console.log('>>>[getTemplates] Total files:', files.length);

  // filter out files that are ignored
  const filteredFiles = files;
  console.log('>>>[getTemplates] Files being passed to assistant:', filteredFiles.length);

  const assistantMessage = assistantMessageBuilder
    ? assistantMessageBuilder({
        repoInfo,
        filteredFiles,
        isAssetFile,
        isExamplePrompt,
      })
    : `
  <dualiteArtifact id="${repoInfo.repo || 'github-project'}" title="${repoInfo.repo || 'Github Project'}" type="bundled">
  ${filteredFiles
    .map((file: any) => {
      return `<dualiteAction type="file" "${file.dualiteIgnore ? '.dualiteIgnore' : ''}" filePath="${file.path}">
  ${file.content}
  </dualiteAction>`;
    })
    .join('\n')}
    ${
      isExamplePrompt
        ? `
    <dualiteAction type="shell"> yarn install </dualiteAction>
    <dualiteAction type="shell"> yarn run dev </dualiteAction>
    `
        : ''
    }
  </dualiteArtifact>
  ${
    isExamplePrompt
      ? `Here's what I've built for you!

I've created a clean and fully functional solution tailored to your request. It's designed with usability, clarity, and performance in mind.

✨ Key Features:

Fully responsive and intuitive user interface
Lightweight, fast, and optimized for all devices

🎨 Design Highlights:

Modern, minimal aesthetic with smooth transitions
Accessible and mobile-friendly layout
Thoughtful use of icons and visual cues for clarity
Modular, maintainable code structure with scalability in mind

Whether you're viewing this on mobile or desktop, the experience remains seamless and visually appealing. Feel free to customize or extend it further based on your needs!`
      : ''
  }
  `;

  // Don't log the entire assistantMessage as it could be` very large
  console.log(`[getTemplates] Assistant message generated (${assistantMessage.length} chars)`);

  // console.log(`Generated assistant message`, assistantMessage);

  let userMessage = ``;

  userMessage += `
  ---
  The code files have been imported successfully.
  
  REPOSITORY STRUCTURE:
  Here's a list of all files in the repository:
  ${filteredFiles.map((file) => `- ${file.path}`).join('\n')}
  
  Here are your next steps:
  DEVELOPMENT SETUP (Using given package manager):
  1. Install project dependencies
  2. Start development server

  ${
    framework === 'react_shadcn'
      ? `COMPONENT SETUP:
  To use Shadcn UI components, install them first:
  \`npx --yes shadcn@latest add <component-1-name> <component-2-name>\`
  `
      : ''
  }

  DEVELOPMENT GUIDELINES:
  • You can modify any existing files
  • Use any imported components and utilities
  • Follow the project's coding standards

  Please proceed with the implementation based on the requirements provided.
  ---
  `;

  userMessage += `
  NOTE: FOLLOWING FILES ARE PRESENT IN THE SYSTEM BUT NOT SHOWN TO YOU BUT YOU CAN IMPORT THEM AND USE THEM IN YOUR APP:
${filteredFiles
  .reduce((acc, file) => {
    if (file.dualiteIgnore) {
      acc.push(`- ${file.path}`);
    }

    return acc;
  }, [] as string[])
  .join('\n')}

  Permitted actions:
  ✓ Import these files and use them in your app

  If you need to make changes to functionality, create new files instead of modifying the protected ones listed above.
  ---
  `;

  console.log(`[getTemplates] User message generated (${userMessage.length} chars)`);
  console.log(`[getTemplates] Template processing completed in ${Date.now() - startTime}ms`);

  return {
    assistantMessage,
    userMessage,
  };
}

async function fetchRepositoryContentViaAPI(
  repoUrl: string,
  repoInfo: { owner: string; repo: string; branch: string },
): Promise<Array<{ path: string; content: string; name: string; dualiteIgnore: boolean }> | undefined> {
  try {
    console.log(
      `[fetchRepositoryContentViaAPI] Fetching content for ${repoInfo.owner}/${repoInfo.repo}@${repoInfo.branch}`,
    );

    // Call server-side API endpoint for GitHub repository content
    const response = await axios.get(`/api/github-example-prompts`, {
      params: {
        owner: repoInfo.owner,
        repo: repoInfo.repo,
        branch: repoInfo.branch,
      },
    });

    if (response.status !== 200) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = response.data;

    if (data.error) {
      throw new Error(`API returned error: ${data.error}`);
    }

    if (!data.files || !Array.isArray(data.files)) {
      throw new Error('API response did not contain files array');
    }

    console.log(`[fetchRepositoryContentViaAPI] Successfully fetched ${data.files.length} files via API`);

    // Process files and add to workbench store
    const processedFiles: Array<{ path: string; content: string; name: string; dualiteIgnore: boolean }> = [];

    const artifactId = `${repoInfo.repo}`;

    /*
     * workbenchStore.addArtifact({
     *   id: artifactId,
     *   title: artifactId || 'GitHub Import',
     *   messageId: 'github-files-import',
     * });
     */

    for (const file of data.files) {
      if (file.path.includes('node_modules')) {
        console.log(`Skipping hidden or unnecessary file: ${file.path}`);
        continue;
      }

      const isIgnored = CHECK_IGNORE_FILE(file.path);

      processedFiles.push({
        name: file.path,
        path: file.path,
        content: typeof file.content === 'string' ? sanitizePostgresString(file.content) : file.content,
        dualiteIgnore: isIgnored,
      });

      /*
       * workbenchStore.addAction(actionData);
       * workbenchStore.runAction(actionData);
       */
    }

    return processedFiles;
  } catch (error) {
    console.error(`[fetchRepositoryContentViaAPI] Error:`, error);

    // Fallback to direct GitHub API if server API fails
    console.log(`[fetchRepositoryContentViaAPI] Falling back to direct GitHub API`);
    return fetchRepositoryContent(repoUrl, repoInfo);
  }
}
